import React from "react";
import { motion } from "framer-motion";
import SectionReveal from "./SectionReveal";

const USE_CASES = [
  {
    id: "promo",
    number: "01",
    title: "Promo / Sales / Explainers",
    tag: "PROMO / EXPLAINER",
    media: {
      type: "vimeo",
      vimeoId: "1205887734",
      vimeoHash: "77b206592c",
    },
    body: "Got a product to sell or an idea worth sharing? We craft sharp, attention-grabbing videos using your footage or fully generated visuals, tailored to your brand. Add professional graphics, music, and voice-over, and you have a compelling short-form video ready to perform across any digital platform.",
  },
  {
    id: "interviews",
    number: "02",
    title: "Interviews",
    tag: "INTERVIEW",
    media: {
      type: "vimeo",
      vimeoId: "1205887735",
      vimeoHash: "f1b1b2b816",
    },
    body: "Turn a raw interview into a powerful story. We clean, upscale, and polish your audio for a broadcast-quality finish, swap out distracting backgrounds, and weave in supporting visuals to keep viewers engaged from start to finish — perfect for corporate communications and B2B marketing.",
  },
  {
    id: "social",
    number: "03",
    title: "Social Media",
    tag: "SOCIAL",
    media: {
      type: "vimeo",
      vimeoId: "1205887733",
      vimeoHash: "46cb07e2a1",
    },
    body: "Need a steady stream of polished content to keep your brand visible and engaging across social platforms? We take your existing footage or generate fresh video tailored to your brand, cutting it down into engaging short-form content optimized for Instagram, LinkedIn, TikTok, and beyond. With branded graphics, captions, and music, we deliver ready-to-post videos that build your audience and keep your feed looking consistently professional.",
  },
];

const UseCaseMedia = ({ media, title }) => {
  if (media.type === "vimeo") {
    // Vimeo background mode letterboxes (object-fit: contain) inside the iframe,
    // so we size the iframe to the video's native 16:9 and scale it to fully
    // cover the 4:3 card. Width 133.34% of a 4:3 box yields an exact 16:9 frame;
    // overflow-hidden on the parent crops the horizontal overflow.
    return (
      <iframe
        src={`https://player.vimeo.com/video/${media.vimeoId}?h=${media.vimeoHash}&background=1&autoplay=1&loop=1&muted=1`}
        title={title}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        loading="lazy"
        aria-label={title}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-1000 group-hover:scale-110"
        style={{ width: "133.34%", height: "100%" }}
        data-testid="use-case-video"
      />
    );
  }
  if (media.type === "video") {
    return (
      <video
        src={media.src}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-label={title}
        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
        data-testid="use-case-video"
      />
    );
  }
  return (
    <img
      src={media.src}
      alt={title}
      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
      loading="lazy"
    />
  );
};

const UseCases = () => {
  return (
    <section
      id="use-cases"
      data-testid="use-cases-section"
      className="relative bg-[#050505] py-28 sm:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <SectionReveal className="mb-16 sm:mb-24 max-w-4xl">
          <span className="block text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6">
            04 — Use Cases
          </span>
          <h2
            className="text-4xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.95] text-white"
            data-testid="use-cases-headline"
          >
            What we
            <br />
            make for
            <br />
            <span className="text-zinc-500">our clients</span>
          </h2>
        </SectionReveal>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {USE_CASES.map((uc, idx) => (
            <motion.article
              key={uc.id}
              data-testid={`use-case-card-${uc.id}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.9,
                delay: idx * 0.15,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group relative bg-[#0A0A0A] border border-white/10 flex flex-col hover:border-white/30 transition-colors duration-500"
            >
              <div className="relative w-full aspect-[4/3] overflow-hidden">
                <UseCaseMedia media={uc.media} title={uc.title} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                <span className="absolute top-5 left-5 text-xs font-bold uppercase tracking-[0.3em] text-zinc-300">
                  {uc.number}
                </span>
              </div>

              <div className="p-7 sm:p-9 flex flex-col flex-grow">
                <h3
                  className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mb-5"
                  data-testid={`use-case-title-${uc.id}`}
                >
                  {uc.title}
                </h3>
                <p
                  className="text-sm sm:text-base font-light text-zinc-400 leading-relaxed flex-grow"
                  data-testid={`use-case-body-${uc.id}`}
                >
                  {uc.body}
                </p>

                <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    {uc.tag}
                  </span>
                  <span
                    aria-hidden="true"
                    className="w-8 h-px bg-white/20 group-hover:w-16 group-hover:bg-white transition-all duration-500"
                  />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCases;
